"use client";

import React, { useEffect, useRef } from "react";
import { withSafeRender } from "../../atoms/safe-render";

/* The name is a literal, and `grid-section.tsx`'s `GRID_SECTION_OFFSET_PROPERTY` is the same
   string. */
const SECTION_OFFSET_PROPERTY = "--cl-section-offset";

/**
 * The pinned band above this section in CSS pixels, taken from the cascade —
 * `sticky-offset.client.tsx` publishes the measured grid-header height on the scope around the
 * whole view, and every summary inherits it.
 */
function readSectionOffset(summary: Element): number {
  const pixels = Number.parseFloat(
    getComputedStyle(summary).getPropertyValue(SECTION_OFFSET_PROPERTY)
  );
  /* Unset, or a value this cannot compare against a viewport coordinate: the
     band is 0, which is the stylesheet's own `var()` fallback. */
  return Number.isFinite(pixels) ? pixels : 0;
}

/**
 * GS8's last clause — "when a pinned section is closed, the page scrolls so its header stays in
 * view". Everything else in GS8 is already free: the pin is `position: sticky` and the chevron is
 * CSS off `details[open]`, both in `grid-section.module.css`.
 */
export interface SectionScrollKeeperProps {
  /**
   * Layout only — the zero-size class. Required in practice: the span must contribute no layout,
   * and this component owns no stylesheet.
   */
  className?: string;
  /**
   * How far below the viewport top the grid header already sits, in CSS pixels. A summary scrolled
   * above this line is behind the header rather than on screen.
   */
  offset?: number;
}

function SectionScrollKeeperImpl({ className, offset }: SectionScrollKeeperProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    /* The `<details>` this span was rendered immediately before. */
    const details = anchorRef.current?.nextElementSibling;
    if (!details || details.tagName !== "DETAILS") {
      return undefined;
    }

    const onToggle = () => {
      if ((details as HTMLDetailsElement).open) {
        return;
      }
      const summary = details.querySelector("summary");
      /* Only when the header has actually been carried above the pinned grid header. A section
         closed while fully on screen must not move the page — scrolling a reader who did not ask to
         be scrolled is worse than the clause this fixes. */
      if (!summary) {
        return;
      }
      if (summary.getBoundingClientRect().top >= (offset ?? readSectionOffset(summary))) {
        return;
      }
      /* Instant, deliberately: this is a correction to where the page already
         is, not a journey. Nothing animates, so AX10's
         `prefers-reduced-motion` has nothing to suppress. */
      summary.scrollIntoView({ block: "start" });
    };

    /* `toggle` does not bubble, so it is bound to the element itself. */
    details.addEventListener("toggle", onToggle);
    return () => details.removeEventListener("toggle", onToggle);
  }, [offset]);

  return <span aria-hidden="true" className={className} ref={anchorRef} />;
}
export const SectionScrollKeeper = withSafeRender(SectionScrollKeeperImpl, "SectionScrollKeeper");

export default SectionScrollKeeper;
